import type { ContextKind, ContextMismatch, ContextProbe, Evidence } from '@fraudeai/schema';
import { defineRule, type Rule } from '../types.js';

/**
 * Правила по расхождениям между исполнительными контекстами.
 *
 * Самая доказательная группа во всём наборе. Настоящий браузер физически не
 * может ответить по-разному на один и тот же вопрос в главном потоке и в
 * воркере — это один процесс и один набор системных данных. Расхождение здесь
 * означает ровно одно: часть контекстов пропатчена, а часть нет.
 *
 * Отсюда высокие веса и отсутствие скидок на confidence.
 */

const CONTEXT_NAMES: Record<string, string> = {
  main: 'главный поток',
  worker: 'Web Worker',
  sharedWorker: 'Shared Worker',
  serviceWorker: 'Service Worker',
  iframe: 'iframe',
  iframeSandboxed: 'iframe в песочнице',
  iframeBlank: 'iframe с srcdoc',
};

function describeContext(kind: string): string {
  return CONTEXT_NAMES[kind] ?? kind;
}

function format(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

/**
 * Контексты с собственным потоком исполнения.
 *
 * Различать их от iframe принципиально важно для вывода. Антидетект-сборка
 * патчит главный поток и забывает про воркеры — там у неё отдельный realm,
 * до которого подмена не доезжает. А вот в iframe регулярно инжектят скрипты
 * обычные расширения: блокировщики рекламы, менеджеры паролей, защита
 * приватности. Поэтому расхождение с воркером — почти наверняка подмена,
 * а расхождение только во фреймах чаще всего означает расширение.
 *
 * Раньше оба случая давали одинаковый вердикт с ярлыком «антидетект-браузер».
 * Это было несправедливо: пользователь без всякого антидетекта получал
 * обвинение и не имел возможности его проверить.
 */
const WORKER_CONTEXTS: ContextKind[] = ['worker', 'sharedWorker', 'serviceWorker'];

function involvesWorker(hits: ContextMismatch[]): boolean {
  return hits.some(
    (m) => WORKER_CONTEXTS.includes(m.contextA) || WORKER_CONTEXTS.includes(m.contextB),
  );
}

const SOFTER: Record<string, 'high' | 'medium'> = { critical: 'high', high: 'medium' };

function firefoxPrivacyContext(
  signals: Parameters<Rule['evaluate']>[0],
  id: string,
  hits: ContextMismatch[],
  evidence: Evidence[],
): ReturnType<Rule['evaluate']> {
  if (signals.navigator?.parsed?.engine !== 'Gecko') return null;

  const privacy =
    (id === 'ctx.hardware' &&
      hits.every((hit) => hit.field === 'hardwareConcurrency' && String(hit.valueA) === '2')) ||
    (id === 'ctx.screen' &&
      hits.every((hit) => hit.field === 'colorDepth' && String(hit.valueA) === '24'));

  if (!privacy) return null;

  return {
    severity: 'low',
    confidence: 0.45,
    flags: ['PRIVACY_TOOLING'],
    explanation:
      'Firefox и расширения приватности могут урезать аппаратные значения в главном ' +
      'контексте сильнее, чем во фреймах или service worker. Расхождение остаётся ' +
      'видимым и снижает полноту проверки, но для Gecko это больше похоже на ' +
      'антифингерпринтинг, чем на антидетект-браузер.',
    evidence,
  };
}

/** Пояснение для случая, когда разошлись только фреймы. */
function iframeOnlyNote(): string {
  return (
    ' Расхождение замечено только во встроенных фреймах, но не в воркерах. ' +
    'Чаще всего так ведёт себя расширение браузера, которое внедряет свой ' +
    'скрипт в одни фреймы и не внедряет в другие: блокировщик рекламы, ' +
    'менеджер паролей, защита приватности. Антидетект-сборки, наоборот, ' +
    'обычно выдают себя именно на воркерах. Если расширений нет, стоит ' +
    'присмотреться внимательнее.'
  );
}

/** Собирает правило для группы полей пробы. */
function mismatchRule(options: {
  id: string;
  title: string;
  fields: (keyof ContextProbe)[];
  severity: 'critical' | 'high' | 'medium';
  weight: number;
  explain: (m: ContextMismatch) => string;
  /** Дополнительные улики — например, какое именно сообщение разошлось. */
  detail?: (signals: Parameters<Rule['evaluate']>[0], hits: ContextMismatch[]) => Evidence[];
}): Rule {
  return defineRule({
    meta: {
      id: options.id,
      title: options.title,
      severity: options.severity,
      category: 'inconsistency',
      flags: ['SPOOFING', 'INCONSISTENT'],
      weight: options.weight,
      evidenceTier: 'corroborating',
      evidenceGroup: options.id,
      requires: ['contexts'],
    },
    evaluate(signals) {
      const hits = (signals.contexts?.mismatches ?? []).filter((m) =>
        options.fields.includes(m.field),
      );
      if (hits.length === 0) return null;

      const workerInvolved = involvesWorker(hits);
      const first = hits[0];

      const evidence: Evidence[] = hits.slice(0, 6).map((m) => ({
        label: `${m.field}: ${describeContext(m.contextA)} против ${describeContext(m.contextB)}`,
        expected: format(m.valueA),
        actual: format(m.valueB),
      }));

      if (options.detail) {
        evidence.push(...options.detail(signals, hits));
      }

      const firefoxPrivacy = firefoxPrivacyContext(signals, options.id, hits, evidence);
      if (firefoxPrivacy) return firefoxPrivacy;

      if (workerInvolved) {
        return { explanation: options.explain(first), evidence };
      }

      // Только фреймы: вердикт мягче, ярлык «антидетект» не вешаем.
      return {
        severity: SOFTER[options.severity] ?? options.severity,
        confidence: 0.6,
        flags: ['INCONSISTENT'],
        explanation: options.explain(first) + iframeOnlyNote(),
        evidence,
      };
    },
  });
}

/**
 * Находит конкретное сообщение об ошибке, которое разошлось.
 *
 * Без этого отчёт показывал два хеша и предлагал поверить на слово —
 * ровно то, чего чекер обещал не делать.
 */
function errorSampleDetail(
  signals: Parameters<Rule['evaluate']>[0],
  hits: ContextMismatch[],
): Evidence[] {
  const probes = signals.contexts?.probes;
  if (!probes) return [];

  const hit = hits.find((m) => m.field === 'errorSignature');
  if (!hit) return [];

  const a = probes[hit.contextA]?.errorSamples;
  const b = probes[hit.contextB]?.errorSamples;
  if (!a || !b) return [];

  const out: Evidence[] = [];
  const length = Math.max(a.length, b.length);
  for (let i = 0; i < length && out.length < 3; i++) {
    if (a[i] === b[i]) continue;
    out.push({
      label: `Сообщение об ошибке №${i + 1}`,
      expected: a[i] ?? '—',
      actual: b[i] ?? '—',
    });
  }
  return out;
}

export const crossContextRules: Rule[] = [
  mismatchRule({
    id: 'ctx.user-agent',
    title: 'User-Agent различается между контекстами',
    fields: ['userAgent'],
    severity: 'critical',
    weight: 45,
    explain: (m) =>
      `В ${describeContext(m.contextB)} браузер сообщает не тот User-Agent, что в ` +
      `${describeContext(m.contextA)}. Настоящий браузер отдаёт одну и ту же строку везде — ` +
      'значит, подмена поставлена только в главный поток страницы.',
  }),

  mismatchRule({
    id: 'ctx.platform',
    title: 'Платформа различается между контекстами',
    fields: ['platform', 'uaDataPlatform', 'uaDataMobile', 'vendor'],
    severity: 'critical',
    weight: 40,
    explain: (m) =>
      `Заявленная платформа в ${describeContext(m.contextB)} не совпадает с ` +
      `${describeContext(m.contextA)}. Операционная система у процесса одна, ` +
      'разных ответов быть не может.',
  }),

  mismatchRule({
    id: 'ctx.hardware',
    title: 'Параметры железа различаются между контекстами',
    fields: ['hardwareConcurrency', 'deviceMemory'],
    severity: 'critical',
    weight: 40,
    explain: (m) =>
      `Число ядер или объём памяти в ${describeContext(m.contextB)} отличается от ` +
      `${describeContext(m.contextA)}. Железо у машины одно; расхождение появляется, ` +
      'когда значение подменено в одном контексте и оставлено настоящим в другом.',
  }),

  mismatchRule({
    id: 'ctx.webgl',
    title: 'Видеокарта различается между контекстами',
    fields: ['webglRenderer', 'webglVendor'],
    severity: 'critical',
    weight: 40,
    explain: (m) =>
      `${describeContext(m.contextB)} видит другую видеокарту, чем ` +
      `${describeContext(m.contextA)}. Драйвер и GPU общие для всего процесса.`,
  }),

  mismatchRule({
    id: 'ctx.engine',
    title: 'Поведение движка JavaScript различается между контекстами',
    fields: ['mathSignature', 'errorSignature', 'stackShape'],
    severity: 'critical',
    weight: 45,
    detail: errorSampleDetail,
    explain: (m) =>
      `Математика или тексты ошибок в ${describeContext(m.contextB)} отличаются от ` +
      `${describeContext(m.contextA)}. Это один и тот же движок JavaScript — расхождение ` +
      'означает, что встроенные функции кем-то переопределены.',
  }),

  mismatchRule({
    id: 'ctx.timezone',
    title: 'Часовой пояс различается между контекстами',
    fields: ['timezone', 'timezoneOffset'],
    severity: 'high',
    weight: 30,
    explain: (m) =>
      `Часовой пояс в ${describeContext(m.contextB)} не совпадает с ` +
      `${describeContext(m.contextA)}. Типичный след подмены геолокации, ` +
      'доехавшей не до всех контекстов.',
  }),

  mismatchRule({
    id: 'ctx.language',
    title: 'Язык различается между контекстами',
    fields: ['language', 'languages'],
    severity: 'high',
    weight: 25,
    explain: (m) =>
      `Языковые настройки в ${describeContext(m.contextB)} отличаются от ` +
      `${describeContext(m.contextA)}.`,
  }),

  mismatchRule({
    id: 'ctx.screen',
    title: 'Параметры экрана различаются между контекстами',
    fields: ['screenWidth', 'screenHeight', 'colorDepth', 'devicePixelRatio'],
    severity: 'high',
    weight: 30,
    explain: (m) =>
      `Разрешение или глубина цвета в ${describeContext(m.contextB)} отличаются от ` +
      `${describeContext(m.contextA)}.`,
  }),

  mismatchRule({
    id: 'ctx.webdriver',
    title: 'Флаг webdriver различается между контекстами',
    fields: ['webdriver'],
    severity: 'critical',
    weight: 40,
    explain: (m) =>
      `Признак автоматизации скрыт в ${describeContext(m.contextA)}, но виден в ` +
      `${describeContext(m.contextB)}. Так выглядит попытка спрятать драйвер от страницы.`,
  }),

  defineRule({
    meta: {
      id: 'ctx.canvas',
      title: 'Отрисовка canvas различается между контекстами',
      severity: 'high',
      category: 'inconsistency',
      flags: ['SPOOFING', 'INCONSISTENT'],
      weight: 25,
      evidenceTier: 'corroborating',
      evidenceGroup: 'ctx.canvas',
      requires: ['contexts'],
    },
    evaluate(signals) {
      const hits = (signals.contexts?.mismatches ?? []).filter((m) => m.field === 'canvasHash');
      if (hits.length === 0) return null;

      return {
        // Не абсолютная уверенность: в отдельных сборках растеризация в
        // воркере и в главном потоке действительно может идти разными путями.
        confidence: 0.75,
        explanation:
          'Одна и та же картинка, отрисованная в разных контекстах, дала разный результат. ' +
          'Чаще всего это означает, что защита canvas включена не везде.',
        evidence: hits.slice(0, 4).map((m) => ({
          label: `canvas: ${describeContext(m.contextA)} против ${describeContext(m.contextB)}`,
          expected: String(m.valueA),
          actual: String(m.valueB),
        })),
      };
    },
  }),

  defineRule({
    meta: {
      id: 'ctx.workers-unavailable',
      title: 'Браузер заблокировал проверку в worker-контекстах',
      severity: 'low',
      category: 'privacy',
      flags: ['PRIVACY_TOOLING'],
      weight: 6,
      requires: ['contexts', 'navigator'],
    },
    evaluate(signals) {
      const parsed = signals.navigator?.parsed;
      const unavailable = signals.contexts?.unavailable ?? [];
      if (
        parsed?.engine !== 'Blink' ||
        parsed.isMobile ||
        parsed.platformFamily === 'android' ||
        parsed.platformFamily === 'ios' ||
        !unavailable.includes('worker')
      ) {
        return null;
      }

      return {
        confidence: unavailable.includes('sharedWorker') ? 0.45 : 0.35,
        explanation:
          'В настольном Chromium обычный Web Worker должен запускаться на странице нашего домена. ' +
          'Когда он недоступен, браузер лишает проверку сверки тех же API в другом JavaScript-контексте. ' +
          'Это снижает полноту проверки и бывает у расширений приватности, политик браузера и ' +
          'антидетект-сборок. Само по себе отсутствие worker-контекста больше не считается ' +
          'доказательством подмены браузера.',
        evidence: [
          { label: 'Браузер', actual: `${parsed.browser} / ${parsed.platformFamily}` },
          { label: 'Недоступно', detail: unavailable.map(describeContext).join(', ') },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'ctx.unavailable',
      title: 'Часть контекстов недоступна для проверки',
      severity: 'low',
      category: 'privacy',
      flags: ['PRIVACY_TOOLING'],
      weight: 5,
      requires: ['contexts'],
    },
    evaluate(signals) {
      const contexts = signals.contexts;
      if (!contexts) return null;

      // Shared Worker не поддерживается в Safari, Service Worker требует
      // отдельной настройки — это ожидаемо и не должно наказывать.
      const meaningful = contexts.unavailable.filter(
        (kind) => kind !== 'sharedWorker' && kind !== 'serviceWorker',
      );
      if (meaningful.length === 0) return null;

      return {
        confidence: 0.5,
        explanation:
          'Не удалось выполнить проверку в части контекстов: ' +
          `${meaningful.map(describeContext).join(', ')}. Их блокировка сама по себе ` +
          'встречается у расширений приватности, но также лишает нас самой надёжной проверки.',
        evidence: [{ label: 'Недоступные контексты', detail: meaningful.map(describeContext).join(', ') }],
      };
    },
  }),
];
